import React, { memo, useMemo } from "react";
import { Box, Typography, Card, useTheme, Tooltip } from "@mui/material";
import { GEOLOGICAL_UNIT_COLORS } from "../constants";

/**
 * Geological cross-section component following industry standards
 * Displays a to-scale vertical representation of geological units
 */
const GeologicalCrossSection = memo(({ data, surfaceElevation }) => {
  const theme = useTheme();

  // Calculate display parameters
  const displayParams = useMemo(() => {
    if (!data || data.length === 0) return null;

    const elevations = data.flatMap((d) => [d.top_elev, d.bottom_elev]);
    const maxElev = Math.max(...elevations, surfaceElevation || 0);
    const minElev = Math.min(...elevations);
    const totalDepth = maxElev - minElev;

    const MIN_HEIGHT = 300;
    const MAX_HEIGHT = 790;
    const PIXELS_PER_FOOT = 2;

    let calculatedHeight = totalDepth * PIXELS_PER_FOOT;
    calculatedHeight = Math.min(
      MAX_HEIGHT,
      Math.max(MIN_HEIGHT, calculatedHeight)
    );

    // 🖨️ If printing, subtract vertical padding (48px total from padding: 3 top/bottom)
    const isPrinting =
      typeof window !== "undefined" && window.matchMedia?.("print").matches;
    const paddingAdjustment = isPrinting ? 48 : 0;

    const BORE_LOG_HEIGHT = calculatedHeight - paddingAdjustment;
    const BORE_LOG_WIDTH = 120;
    const scale = BORE_LOG_HEIGHT / totalDepth;

    return {
      maxElev,
      minElev,
      totalDepth,
      scale,
      BORE_LOG_HEIGHT,
      BORE_LOG_WIDTH,
    };
  }, [data, surfaceElevation]);

  if (!displayParams) return null;

  const { maxElev, minElev, scale, BORE_LOG_HEIGHT, BORE_LOG_WIDTH } =
    displayParams;

  return (
    <Box
      sx={{
        "@media print": {
          pageBreakBefore: "always",
          paddingTop: 3,
        },
      }}
    >
      <Card
        variant="outlined"
        sx={{
          p: 3,
          mt: 3,
          "@media print": {
            pageBreakBefore: "always",
          },
        }}
      >
        <Typography variant="h6" mb="40px">
          Geological Cross-Section (To Scale)
        </Typography>

        <Box
          sx={{
            display: "flex",
            gap: 3,
            justifyContent: "center",
            alignItems: "flex-start",
            minHeight: BORE_LOG_HEIGHT + 10, // Add padding for labels
          }}
        >
          {/* Elevation scale */}
          <Box
            sx={{ width: 120, position: "relative", height: BORE_LOG_HEIGHT }}
          >
            <Typography
              variant="caption"
              sx={{
                position: "absolute",
                top: -30,
                left: 0,
                fontWeight: "bold",
                fontSize: "0.75rem",
              }}
            >
              Elevation (ft msl)
            </Typography>

            {/* Smart tick marks based on depth range */}
            {(() => {
              const depthRange = maxElev - minElev;
              const tickInterval =
                depthRange > 500 ? 100 : depthRange > 200 ? 50 : 25;
              const startElev =
                Math.floor(maxElev / tickInterval) * tickInterval;
              const endElev = Math.ceil(minElev / tickInterval) * tickInterval;

              return Array.from(
                {
                  length: Math.floor((startElev - endElev) / tickInterval) + 1,
                },
                (_, i) => {
                  const elev = startElev - i * tickInterval;
                  if (elev < minElev || elev > maxElev) return null;
                  const y = (maxElev - elev) * scale;

                  return (
                    <Box
                      key={elev}
                      sx={{
                        position: "absolute",
                        top: y,
                        left: 0,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Box
                        sx={{
                          width: 12,
                          height: 1,
                          bgcolor: "text.primary",
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          ml: 1,
                          fontSize: "0.7rem",
                          fontWeight: "bold",
                        }}
                      >
                        {elev}
                      </Typography>
                    </Box>
                  );
                }
              );
            })()}
          </Box>

          {/* Bore log */}
          <Box sx={{ position: "relative", height: BORE_LOG_HEIGHT }}>
            <Box
              sx={{
                width: BORE_LOG_WIDTH,
                height: BORE_LOG_HEIGHT,
                border: `2px solid ${theme.palette.divider}`,
                position: "relative",
                overflow: "hidden",
                bgcolor: "background.paper",
              }}
            >
              {data.map((unit, index) => {
                const top = (maxElev - unit.top_elev) * scale;
                const height = unit.formation_thickness * scale;
                const color =
                  GEOLOGICAL_UNIT_COLORS[unit.geologic_unit] || "#ccc";

                return (
                  <Tooltip
                    key={index}
                    title={
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: "bold" }}
                        >
                          {unit.geologic_unit}
                        </Typography>
                        <br />
                        <Typography variant="caption">
                          Top: {unit.top_elev} ft msl
                        </Typography>
                        <br />
                        <Typography variant="caption">
                          Bottom: {unit.bottom_elev} ft msl
                        </Typography>
                        <br />
                        <Typography variant="caption">
                          Thickness: {unit.formation_thickness} ft
                        </Typography>
                      </Box>
                    }
                    placement="right"
                    arrow
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top,
                        left: 0,
                        right: 0,
                        height,
                        bgcolor: color,
                        borderTop: `1px solid ${theme.palette.divider}`,
                        borderBottom:
                          index === data.length - 1
                            ? `1px solid ${theme.palette.divider}`
                            : "none",
                        cursor: "pointer",
                        "&:hover": {
                          opacity: 0.8,
                          boxShadow: `inset 0 0 0 2px ${theme.palette.primary.main}`,
                        },
                      }}
                    />
                  </Tooltip>
                );
              })}

              {/* Ground surface indicator */}
              {surfaceElevation && (
                <Box
                  sx={{
                    position: "absolute",
                    top: (maxElev - surfaceElevation) * scale - 2,
                    left: -10,
                    right: -10,
                    height: 4,
                    bgcolor: theme.palette.common.black,
                    zIndex: 10,
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: -8,
                      left: 0,
                      right: 0,
                      height: 8,
                      background: `repeating-linear-gradient(
                      135deg,
                      transparent,
                      transparent 3px,
                      ${theme.palette.common.black} 3px,
                      ${theme.palette.common.black} 6px
                    )`,
                    },
                  }}
                />
              )}
            </Box>

            {/* Unit labels removed - tooltips provide the same information */}
          </Box>

          {/* Depth scale on right */}
          <Box
            sx={{ width: 120, position: "relative", height: BORE_LOG_HEIGHT }}
          >
            <Typography
              variant="caption"
              sx={{
                position: "absolute",
                top: -30,
                right: 0,
                fontWeight: "bold",
                fontSize: "0.75rem",
                whiteSpace: "nowrap",
              }}
            >
              Depth from Surface (ft)
            </Typography>

            {/* Smart depth markers based on depth range */}
            {(() => {
              const depthRange = maxElev - minElev;
              const tickInterval =
                depthRange > 500 ? 100 : depthRange > 200 ? 50 : 25;

              return Array.from(
                { length: Math.floor(depthRange / tickInterval) + 1 },
                (_, i) => {
                  const depth = i * tickInterval;
                  const y = depth * scale;

                  if (y > BORE_LOG_HEIGHT) return null;

                  return (
                    <Box
                      key={depth}
                      sx={{
                        position: "absolute",
                        top: y,
                        right: 0,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          mr: 1,
                          fontSize: "0.7rem",
                          fontWeight: "bold",
                        }}
                      >
                        {depth}
                      </Typography>
                      <Box
                        sx={{
                          width: 12,
                          height: 1,
                          bgcolor: "text.primary",
                        }}
                      />
                    </Box>
                  );
                }
              );
            })()}
          </Box>
        </Box>

        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mt: 2 }}
        >
          Scale: 1 inch = {Math.round((96 / scale) * 10) / 10} feet • Ground
          surface shown with hatched pattern
        </Typography>
      </Card>
    </Box>
  );
});

GeologicalCrossSection.displayName = "GeologicalCrossSection";

export default GeologicalCrossSection;
